import { DiscountProduct } from '@/types/discount';
import { DiscountRateInput } from '../inputs/DiscountRateInput';
import { StockInput } from '../inputs/StockInput';

interface ProductDiscountCardProps {
  product: DiscountProduct;
  type: 'deadline' | 'reservation';
  onDiscountRateChange: (productId: string, discountRate: number) => void;
  onStockChange: (productId: string, stock: number) => void;
  onPriceChange?: (productId: string, price: number) => void;
  onProductSelect: (productId: string) => void;
  isRegistered: boolean;
}

export function ProductDiscountCard({
  product,
  type,
  onDiscountRateChange,
  onStockChange,
  onPriceChange,
  onProductSelect
}: ProductDiscountCardProps) {
  // 카드 배경색 결정
  const getCardBackground = () => {
    if (product.isRegisteredInDB && !product.selected) {
      return 'bg-red-50 border-red-200'; // 삭제 예정 상품 (DB 등록되었지만 체크 해제됨)
    }
    if (product.isRegisteredInDB && product.selected) {
      return 'bg-green-50 border-green-200'; // DB에 등록된 상품
    }
    if (product.selected) {
      return 'bg-blue-50 border-blue-200'; // 새로 선택된 상품
    }
    return 'bg-white border-gray-200'; // 미등록 상품
  };

  // 상태 배지 표시
  const getStatusBadge = () => {
    if (product.isRegisteredInDB && !product.selected) {
      return (
        <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
          저장시 삭제됨
        </span>
      );
    }
    if (product.isRegisteredInDB && product.selected && !product.isModified) {
      return (
        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
          DB 등록됨
        </span>
      );
    }
    if (product.isRegisteredInDB && product.selected && product.isModified) {
      return (
        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium">
          수정됨
        </span>
      );
    }
    if (product.selected && !product.isRegisteredInDB) {
      return (
        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
          신규 선택
        </span>
      );
    }
    return null;
  };

  return (
    <div className={`border rounded-lg p-4 ${getCardBackground()}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-800">{product.name}</h4>
            {getStatusBadge()}
            {product.isRandomBox && (
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
                랜덤박스
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            원가: {product.originalPrice.toLocaleString()}원
          </p>
        </div>
        
        <button
          onClick={() => onProductSelect(product.productId)}
          className={`w-5 h-5 rounded border-2 ${
            product.selected 
              ? 'bg-green-500 border-green-500' 
              : 'bg-white border-gray-300'
          }`}
        >
          {product.selected && (
            <svg className="w-3 h-3 text-white mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      </div>

      {/* 할인 설정 */}
      <div className="space-y-3">
        <DiscountRateInput
          productId={product.productId}
          discountRate={product.discountRate}
          type={type}
          isRandomBox={product.isRandomBox}
          onChange={onDiscountRateChange}
          disabled={!product.selected}
        />

        <StockInput
          productId={product.productId}
          stock={product.stock}
          type={type}
          onChange={onStockChange}
          disabled={!product.selected}
        />

        {/* 랜덤박스 가격 설정 */}
        {product.isRandomBox && onPriceChange && type === 'deadline' && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">랜덤박스 가격</label>
            <input
              type="number"
              value={product.originalPrice}
              onChange={(e) => onPriceChange(product.productId, Number(e.target.value))}
              disabled={!product.selected}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-500"
              min="1000"
              step="100"
            />
          </div>
        )}

        {/* 할인가 표시 */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
          <span className="text-sm text-gray-600">할인가</span>
          <span className="font-semibold text-green-600">
            {product.discountedPrice.toLocaleString()}원
          </span>
        </div>
      </div>
    </div>
  );
}